import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUser } from "@fortawesome/free-regular-svg-icons";
import { ExpandMore } from "@mui/icons-material";
import { signOut } from "firebase/auth";
import { motion, AnimatePresence } from "framer-motion";
import { RootState } from "../../../redux/store/store";
import { clearUser } from "../../../redux/actions/authActions";
import { auth } from "../../firebase";
import { ROLES } from "../../utils/roles";

interface Props {
    onRequireLogin: () => void;
}

const MAX_LENGTH = 18;

const NavbarUserMenu: React.FC<Props> = ({ onRequireLogin }) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [showDropdown, setShowDropdown] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const { userName, authReady, userRole } = useSelector((state: RootState) => state.auth);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (showDropdown && dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setShowDropdown(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [showDropdown]);

    const handlePerfilClick = () => {
        if (!authReady) return;
        if (userName) {
            navigate("/perfil");
        } else {
            onRequireLogin();
        }
    };

    const handleLogout = async () => {
        sessionStorage.setItem("logoutManual", "true");
        await signOut(auth);
        dispatch(clearUser());
        setShowDropdown(false);
        navigate("/");
    };

    if (!authReady) {
        return (
            <div className="mul15">
                <div className="m15c m15c1"></div>
                <div className="m15c m15c2"></div>
                <div className="m15c m15c3"></div>
                <div className="m15c m15c4"></div>
                <div className="m15c m15c5"></div>
            </div>
        );
    }

    if (!userName) {
        return (
            <span onClick={handlePerfilClick} style={{ cursor: "pointer" }}>
                <FontAwesomeIcon className="icon-navbar" title="Perfil" icon={faUser} />
            </span>
        );
    }

    return (
        <div className="usuario-dropdown" ref={dropdownRef}>
            <div className="usuario-toggle" onClick={() => setShowDropdown(!showDropdown)}>
                <span className="usuario-nombre">
                    {userName.length >= MAX_LENGTH + 3 ? `${userName.slice(0, MAX_LENGTH)}...` : userName}
                </span>
                <ExpandMore
                    sx={{
                        ml: 0.2,
                        transition: "transform 0.3s ease",
                        transform: showDropdown ? "rotate(180deg)" : "rotate(0deg)"
                    }}
                />
            </div>

            <AnimatePresence>
                {showDropdown && (
                    <motion.div
                        className="usuario-menu"
                        initial={{ opacity: 0, scale: 0.2, transformOrigin: "top right" }}
                        animate={{ opacity: 1, scale: 1, transformOrigin: "top right" }}
                        exit={{
                            opacity: 0,
                            scale: 0.1,
                            transformOrigin: "top right",
                            transition: { duration: 0.7, ease: [0.4, 0, 0.2, 1] }
                        }}
                        transition={{ duration: 0.35, ease: [0.4, 0, 0.2, 1] }}
                    >
                        <button className="btn-mi-perfil" onClick={() => {
                            navigate("/perfil");
                            setShowDropdown(false);
                        }}>
                            Mi perfil
                        </button>
                        {userRole === ROLES.A12_B && (
                            <button
                                className="btn-mi-perfil"
                                onClick={() => {
                                    navigate("/admin/usuarios");
                                    setShowDropdown(false);
                                }}
                            >
                                Gestión de usuarios
                            </button>
                        )}
                        <button className="btn-cerrar-sesion" onClick={handleLogout}>
                            Cerrar sesión
                        </button>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default NavbarUserMenu;
